<script setup lang="ts">
/**
 * 画像一括追加モーダル。VB版 ConfirmImageListWindow の METHOD_ADD 相当。
 * 複数ファイルを選択し、基準ASINの兄弟ASIN全体(親ASINが同じ商品群)へ
 * まとめて画像を追加する。
 *
 * 実行前に、対象となる兄弟ASIN一覧を代表画像・商品名付きでプレビュー表示する
 * (類似画像モーダル(SimilarImagesModal.vue)と同じカードUIを流用)。
 * 対象は実行時にサーバー側で自動選定されるため、ここでは確認用の表示のみで
 * 個別選択はできない(複数画像ファイルの選択UI・機能は従来どおり維持する)。
 *
 * [画像編集の作り直し] 画像編集画面の「兄弟ASINへコピー」からも開く。
 *   - presetSources: コピーする画像(登録済み画像・候補)。ファイルを選ばなくても実行できる
 *   - excludeBasis : この商品自身には追加しない(この商品の画像は画像編集画面の登録で決めるため)
 */
import { ref, computed } from 'vue'
import { useBulkUploadStagingUrlMutation, useBulkAddSiblingImagesMutation, useSiblingProductsMutation } from '@/api/queries'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useResultModalStore } from '@/stores/resultModal'
import type { BulkAddSiblingImagesRequest } from '@/types/api'

const props = defineProps<{
  asin: string
  regionCode: string
  parentAsin?: string | null
  /** 画像編集画面から渡す、コピーする画像(sourceKey = 画像バケット内のキー) */
  presetSources?: { sourceKey: string; previewUrl: string }[]
  /** この商品自身(基準ASIN)には追加しない */
  excludeBasis?: boolean
}>()
const presets = computed(() => props.presetSources ?? [])
const isCopyMode = computed(() => presets.value.length > 0)

const emit = defineEmits<{ close: [] }>()

const confirmModal = useConfirmModalStore()
const resultModal = useResultModalStore()

const stagingMutation = useBulkUploadStagingUrlMutation()
const addMutation = useBulkAddSiblingImagesMutation()
const siblingsMutation = useSiblingProductsMutation()

// ---- 対象兄弟ASINのプレビュー ----
siblingsMutation.mutate(
  { asin: props.asin, regionCode: props.regionCode, parentAsin: props.parentAsin },
  {
    onError: (err) => resultModal.show(`兄弟ASIN一覧の取得に失敗しました: ${(err as Error).message}`, 'ng'),
  },
)
const siblings = computed(() =>
  (siblingsMutation.data.value?.siblings ?? []).filter((s) => !(props.excludeBasis && s.isBasis)),
)

interface PendingFile {
  file: File
  previewUrl: string
}
const pendingFiles = ref<PendingFile[]>([])

function onFilesSelected(e: Event) {
  const input = e.target as HTMLInputElement
  const files = input.files
  if (!files || files.length === 0) {
    // ファイル選択ダイアログでキャンセルされた場合等。同じファイルを選び直しても
    // changeイベントが発火するよう、ここでも一度inputをリセットしておく
    input.value = ''
    return
  }

  const added: PendingFile[] = Array.from(files).map((file) => ({
    file,
    previewUrl: URL.createObjectURL(file),
  }))
  // pushではなく新しい配列を代入する(Vueのリアクティビティ検知をより確実にするため)
  pendingFiles.value = [...pendingFiles.value, ...added]

  // 同じファイルを連続して選び直してもchangeイベントが発火するように、
  // 最後にinputの値をリセットする。
  input.value = ''
}

function removeFile(index: number) {
  URL.revokeObjectURL(pendingFiles.value[index].previewUrl)
  pendingFiles.value.splice(index, 1)
}

const isBusy = computed(() => stagingMutation.isPending.value || addMutation.isPending.value)

const totalCount = computed(() => presets.value.length + pendingFiles.value.length)

async function onExecute() {
  if (totalCount.value === 0) {
    resultModal.show('追加する画像ファイルを選択してください。', 'ng')
    return
  }
  if (
    !(await confirmModal.ask(
      props.excludeBasis
        ? `${totalCount.value}枚の画像を、${props.asin} の兄弟ASIN(${siblings.value.length}件、この商品を除く)に追加します。よろしいですか？`
        : `選択した${totalCount.value}枚の画像を、${props.asin} と同じ商品の兄弟ASIN全体に追加します。よろしいですか？`,
    ))
  )
    return

  try {
    const stagingFiles: BulkAddSiblingImagesRequest['stagingFiles'] = presets.value.map((p) => ({ sourceKey: p.sourceKey }))
    for (const pf of pendingFiles.value) {
      const staging = await stagingMutation.mutateAsync(pf.file.type || 'image/jpeg')
      await fetch(staging.uploadUrl, {
        method: 'PUT',
        body: pf.file,
        headers: { 'Content-Type': staging.contentType },
      })
      stagingFiles.push({ stagingKey: staging.stagingKey, contentType: staging.contentType })
    }

    addMutation.mutate(
      {
        asin: props.asin,
        regionCode: props.regionCode,
        parentAsin: props.parentAsin,
        stagingFiles,
        excludeBasis: props.excludeBasis || undefined,
      },
      {
        onSuccess: (res) => {
          resultModal.show(
            `${res.targetAsinCount}件のASIN(${res.targetAsins.join(', ')})に、合計${res.addedCount}枚の画像を追加しました。`,
          )
          emit('close')
        },
        onError: (err) => resultModal.show(`追加に失敗しました: ${(err as Error).message}`, 'ng'),
      },
    )
  } catch (err) {
    resultModal.show(`アップロードに失敗しました: ${(err as Error).message}`, 'ng')
  }
}
</script>

<template>
  <div class="bim-overlay" @click="emit('close')">
    <div class="bim-modal" @click.stop>
      <div class="bim-head">
        <div class="bim-title">{{ isCopyMode ? 'この画像を兄弟ASINに一括追加' : '画像をまとめて追加' }}</div>
        <button class="bim-close" @click="emit('close')">
          <svg class="icon" viewBox="0 0 24 24" style="width:18px;height:18px;"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="bim-sub">
        <template v-if="excludeBasis">
          画像を、{{ asin }} と同じ親商品を持つ兄弟ASIN(バリエーション違い等。この商品は除く)の末尾に追加します。
        </template>
        <template v-else>
          選択した画像を、{{ asin }} と同じ親商品を持つ兄弟ASIN全体(バリエーション違い等)へ
          まとめて追加します。
        </template>
      </div>

      <!-- 対象兄弟ASINのプレビュー(SimilarImagesModalと同じカードUI。選択不可・確認用) -->
      <div v-if="siblingsMutation.isPending.value" class="empty-state" style="padding:24px 0;">
        <span class="spinner"></span> 対象ASINを確認中…
      </div>
      <template v-else-if="siblings.length > 0">
        <div class="bim-toolbar">
          <span class="bim-count">追加対象: {{ siblings.length }} 件</span>
        </div>
        <div class="bim-grid">
          <div
            v-for="s in siblings"
            :key="s.asin"
            class="bim-item bim-item-readonly"
            :class="{ basis: s.isBasis }"
          >
            <img v-if="s.imageUrl" :src="s.imageUrl" :alt="s.asin" loading="lazy" />
            <div v-else class="bim-item-noimage">
              <svg class="icon" viewBox="0 0 24 24" style="width:22px;height:22px;"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            </div>
            <div class="bim-item-info">
              <span class="bim-item-asin">{{ s.asin }}<span v-if="s.isBasis" class="bim-basis-tag">基準</span></span>
              <span class="bim-item-name" :title="s.productName ?? ''">{{ s.productName || '(商品名未取得)' }}</span>
            </div>
          </div>
        </div>
      </template>
      <div v-else-if="excludeBasis && siblingsMutation.isSuccess.value" class="empty-state" style="padding:16px 0;">
        この商品には、コピー先の兄弟ASINがありません。
      </div>

      <input
        id="bulk-add-file-input"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        style="display:none"
        @change="onFilesSelected"
      />

      <div class="bim-add-grid">
        <div v-for="(p, idx) in presets" :key="`p${idx}`" class="bim-add-item">
          <img :src="p.previewUrl" alt="コピーする画像" />
        </div>
        <div v-for="(pf, idx) in pendingFiles" :key="idx" class="bim-add-item">
          <img :src="pf.previewUrl" :alt="pf.file.name" />
          <button class="bim-add-remove" title="削除" @click="removeFile(idx)">
            <svg class="icon" viewBox="0 0 24 24" style="width:12px;height:12px;"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>
        <label for="bulk-add-file-input" class="bim-add-picker">
          <svg class="icon" viewBox="0 0 24 24" style="width:26px;height:26px;"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          <span>画像を選択</span>
        </label>
      </div>

      <div class="bim-actions">
        <button class="btn btn-ghost" @click="emit('close')">キャンセル</button>
        <button
          class="btn btn-primary"
          :disabled="isBusy || totalCount === 0 || (excludeBasis && siblings.length === 0)"
          @click="onExecute"
        >
          {{ isBusy ? '処理中…' : `${totalCount}枚を兄弟ASINへ追加` }}
        </button>
      </div>
    </div>
  </div>
</template>
